import store from '@/store'
import { devLog } from '@/utils'
import { getLocation } from '@/utils/io/geolocation'
import { Notify } from 'quasar'
import { moveToForeground } from '@/utils/navigator/backgroundTask'
import Vue from 'vue'
import ptiMixin from '@/utils/pti/ptiMixin'
import { TYPE_AGENT_LEAVE_PREMISES, TYPE_AGENT_RETURN } from '@/consts/events'
import { createNamespacedHelpers } from 'vuex'
import { SERVICE_POST_EVENT_PTI } from '@/store/modules/service/action-types'
import { $ui } from '@/utils/ui'
import {
  PERIMETER_RETURN_TIMEOUT,
  PERIMETER_RETURN_WARNING_TIME,
  PERIMETER_STATUS_ACTIVE,
  PERIMETER_STATUS_DESKTOP,
  PERIMETER_STATUS_INACTIVE,
  PERIMETER_STATUS_LEAVE,
  PERIMETER_STATUS_LIST,
  PERIMETER_STATUS_RETURN,
  PERIMETER_STATUS_SENDING,
  PERIMETER_STATUS_WAIT_FOR_RETURN,
  PERIMETER_STATUS_WARNING,
  PERIMETER_WATCH_INTERVAL
} from '@/consts/pti/perimeterDetector'

const { cordova } = window
const NOTIFICATION_ID = 2

const storeService = createNamespacedHelpers('service')
export default new Vue({
  name: 'PerimeterDetector',
  eventSettingsCheckKey: 'ptiCheckPerimeter',
  statusList: PERIMETER_STATUS_LIST,
  mixins: [ptiMixin],
  data () {
    return {
      distance: 0,
      location: null,
      gpsFailed: 0,
      fakeLocation: null,
      watchInterval: null,
      timeoutCheck: null,
      returnWatcher: null,
      intervalSignal: null,
      sendAlertAt: 0,
      warningNotify: null,
      invalidPermissions: false,
      outOfPerimeterTimes: 0
    }
  },
  computed: {
    ...storeService.mapGetters({
      inSitePerimeter: 'inSitePerimeter',
      serviceActif: 'serviceActif'
    }),
    site () {
      return _.get(this.serviceActif, 'site', {})
    },
    siteLocation () {
      return _.get(this.site, ['address', 'location'])
    },
    siteRadius () {
      return _.get(this.site, ['radius'], 0)
    }
  },
  watch: {
    watchInterval: {
      immediate: true,
      async handler (interval) {
        clearTimeout(this.timeoutCheck)
        this.timeoutCheck = null
        if (interval !== null) {
          return this.updateLocation()
        }
      }
    },
    status: {
      immediate: true,
      async handler (status) {
        switch (status) {
          case PERIMETER_STATUS_INACTIVE.value: {
            this.watchInterval = null
            this.alarmStop()
            break
          }
          case PERIMETER_STATUS_ACTIVE.value: {
            this.watchInterval = PERIMETER_WATCH_INTERVAL
            this.alarmStop()
            break
          }
          case PERIMETER_STATUS_LEAVE.value : {
            this.outOfPerimeterTimes = 1
            this.watchInterval = 1
            break
          }
          case PERIMETER_STATUS_WARNING.value : {
            this.sendAlertAt = new Date().getTime() + PERIMETER_RETURN_TIMEOUT * 1000
            this.watchInterval = 1
            this.alarmPlay({ loop: true })
            break
          }
          case PERIMETER_STATUS_SENDING.value: {
            try {
              this.alarmStop()
              Notify.create({
                message: 'Un signalement de sortie de périmètre à été transmis.',
                caption: 'Veuillez regagner votre site au plus vite',
                color: 'negative',
                timeout: 0,
                multiLine: true,
                actions: [{
                  label: 'Fermer',
                  color: 'white'
                }]
              })
              await this.$store.dispatch(
                'service/' + SERVICE_POST_EVENT_PTI,
                {
                  type: store.getters.constServiceEventTypesKeys[TYPE_AGENT_LEAVE_PREMISES.typeKey]
                }
              )
            } catch (e) {
              devLog('error', e)
            }
            this.status = this.isActive
              ? PERIMETER_STATUS_WAIT_FOR_RETURN.value
              : PERIMETER_STATUS_INACTIVE.value
            break
          }
          case PERIMETER_STATUS_WAIT_FOR_RETURN.value: {
            this.watchInterval = PERIMETER_WATCH_INTERVAL
            break
          }
          case PERIMETER_STATUS_RETURN.value: {
            try {
              await this.$store.dispatch(
                'service/' + SERVICE_POST_EVENT_PTI,
                {
                  type: store.getters.constServiceEventTypesKeys[TYPE_AGENT_RETURN.typeKey]
                }
              )
            } catch (e) {
              devLog('error PERIMETER_STATUS_RETURN ', e)
            }
            this.status = this.isActive
              ? PERIMETER_STATUS_ACTIVE.value
              : PERIMETER_STATUS_INACTIVE.value
            break
          }
        }
      }
    },
    async outOfPerimeterTimes (outOfPerimeterTimes = 0) {
      if (outOfPerimeterTimes === 0) {
        clearTimeout(this.timeoutCheck)
        this.timeoutCheck = null
        this._closeWarning()
      } else if (outOfPerimeterTimes <= PERIMETER_RETURN_WARNING_TIME) {
        devLog(`Agent détecté hors périmètre ${outOfPerimeterTimes} fois`)
        if (outOfPerimeterTimes === PERIMETER_RETURN_WARNING_TIME) {
          this.status = PERIMETER_STATUS_WARNING.value
        }
      } else {
        const time = new Date().getTime()
        const rest = Math.max(0, Math.round((this.sendAlertAt - time) / 1000))
        const timeout = rest === 0
        const message = 'Sortie de périmètre détectée'
        const caption = 'Merci de regagner le périmètre du site.'
        if (cordova) {
          cordova.plugins.notification.local.schedule({
            id: NOTIFICATION_ID,
            color: '#FF0000',
            sound: null,
            title: timeout
              ? 'Sortie de périmètre signalée'
              : `Signalement de sortie de périmètre`,
            text: `${message}\n${caption}`,
            progressBar: { value: Math.floor(rest * 100 / PERIMETER_RETURN_TIMEOUT) }
          })
          moveToForeground()
        }
        if (this.warningNotify === null) {
          this.warningNotify = Notify.create({
            message,
            caption,
            timeout: PERIMETER_RETURN_TIMEOUT * 1000,
            progress: true,
            position: 'center',
            color: 'negative'
          })
        }
        if (timeout) {
          this.status = PERIMETER_STATUS_SENDING.value
        }
        devLog(`Signalement dans ${rest}`)
      }
    },
    async location (l) {
      try {
        await this.computeDistance(l)
        const {
          distance,
          status,
          location = {}
        } = this
        switch (status) {
          case PERIMETER_STATUS_ACTIVE.value: {
            if (location.accuracy > 100) {
              devLog(`Valeur de localisation trop imprécise : Précision ${location.accuracy}`)
              break
            }
            if (distance > 0) {
              devLog('Hors du périmètre du site')
              this.status = PERIMETER_STATUS_LEAVE.value
            }
            break
          }
          case PERIMETER_STATUS_LEAVE.value:
          case PERIMETER_STATUS_WARNING.value: {
            if (distance <= 0) {
              devLog('Retour dans le périmètre')
              this.status = PERIMETER_STATUS_ACTIVE.value
              this.outOfPerimeterTimes = 0
              if (cordova) {
                cordova.plugins.notification.local.clear([NOTIFICATION_ID])
              }
            } else {
              this.outOfPerimeterTimes++
            }
            break
          }
          case PERIMETER_STATUS_WAIT_FOR_RETURN.value: {
            this.outOfPerimeterTimes = 0
            if (distance <= 0) {
              this.status = PERIMETER_STATUS_RETURN.value
            }
            break
          }
        }
      } catch (e) {
        devLog('error PERIMETER_STATUS_WAIT_FOR_RETURN ', e)
      }
    },
    fakeLocation: 'computeDistance'
  },
  methods: {
    _onActiveStateChange (v) {
      if (!(this.$q.platform.is.mobile || $ui.isDev)) {
        this.status = PERIMETER_STATUS_DESKTOP.value
        return
      }
      const active = this.inSitePerimeter
        ? PERIMETER_STATUS_ACTIVE.value
        : PERIMETER_STATUS_WAIT_FOR_RETURN.value
      this.status = v
        ? active
        : PERIMETER_STATUS_INACTIVE.value
    },
    _closeWarning () {
      const { warningNotify } = this
      if (typeof warningNotify === 'function') {
        warningNotify()
        this.warningNotify = null
      }
    },
    async updateLocation () {
      try {
        this.location = await getLocation()
        this.gpsFailed = 0
      } catch (e) {
        ++this.gpsFailed
      }
      if (this.watchInterval && this.timeoutCheck === null) {
        this.timeoutCheck = setTimeout(() => {
          this.timeoutCheck = null
          this.updateLocation()
        }, this.watchInterval * 1000)
        // devLog('New timeout', this.timeoutCheck, 'speed', this.watchInterval)
      }
    },
    async computeDistance (coords) {
      this.distance = await this._getDistanceWithSite(this.fakeLocation || coords)
    },
    /**
     * Retourne la distance estimée à laquelle se trouve la position par rapport à la périphérie du site
     * @returns {Promise<number>}
     * @private
     */
    async _getDistanceWithSite (location) {
      const expectedLocation = this.siteLocation
      if (!(expectedLocation || location)) {
        return 0
      }
      const radius = this.siteRadius
      const {
        latitude,
        longitude,
        accuracy = 50
      } = location
      if (typeof window.lwfGoogleMapsPromise === 'undefined') {
        return -1
      }
      const { maps } = await window.lwfGoogleMapsPromise
      const _distance = maps.geometry.spherical.computeDistanceBetween(
        new maps.LatLng(expectedLocation.latitude, expectedLocation.longitude),
        new maps.LatLng(latitude, longitude)
      )
      return _distance - radius - accuracy
    }
  }
})
